import React from 'react';

import { useApolloClient } from '@apollo/client';
import isEqual from 'lodash/isEqual';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { CurrentlyPlaying } from 'components/ConnectedAudioPlayer/hls-helper';

import {
  GetStoryAudioUrlDocument,
  GetStoryAudioUrlQuery,
  useIncreaseNumberOfListensMutation,
} from 'graphql/generated/graphql';

import { useAuth } from 'hooks/useAuth';
import useGraphaelDefaults from 'hooks/useGraphaelDefaults';

import {
  hideFullPlayer,
  PlayerState,
  requestSeekBackwardThunk,
  requestSeekForwardThunk,
  requestSeekToTimeThunk,
  requestTogglePlayThunk,
  resetFullAudioPlayed,
  resetPlayerThunk,
  showFullPlayer,
  updateCurrentlyPlaying,
} from 'store/modules/player';
import ReduxState from 'store/ReduxState';

import { ContentType } from 'types/contentType';

type Audio = Pick<CurrentlyPlaying, 'id'>;
type PlaySpeakerTeaser = Pick<
  CurrentlyPlaying,
  'id' | 'audioUrl' | 'speakerName' | 'trackingCategory'
>;
type PlayStoryTeaser = Pick<
  CurrentlyPlaying,
  'id' | 'audioUrl' | 'storyTitle' | 'speakerName' | 'trackingCategory'
>;
type PlayOtherTeaser = Pick<
  CurrentlyPlaying,
  'id' | 'audioUrl' | 'otherTitle' | 'trackingCategory'
>;
type PlayStoryAudio = Pick<
  CurrentlyPlaying,
  | 'id'
  | 'storyTitle'
  | 'speakerName'
  | 'algoliaSource'
  | 'orgasms'
  | 'contentType'
> & { audioUrl?: string };

export const usePlayer = () => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const { isAuthenticated } = useAuth();

  const player = useSelector<ReduxState, PlayerState>(
    ({ player }) => player,
    isEqual,
  );

  const isMyAudio = React.useCallback(
    (audio: Audio): boolean => {
      return player.currentlyPlaying?.id === audio.id;
    },
    [player],
  );

  const togglePlay = React.useCallback(() => {
    dispatch(requestTogglePlayThunk());

    if (player.fullAudioPlayed) {
      dispatch(resetFullAudioPlayed());
    }
  }, [dispatch, player.fullAudioPlayed]);

  const stopPlayback = React.useCallback(() => {
    dispatch(resetPlayerThunk());
  }, [dispatch]);

  const [increasePlays] = useIncreaseNumberOfListensMutation(
    useGraphaelDefaults(),
  );

  const playAudio = React.useCallback(
    (audio: CurrentlyPlaying) => {
      if (
        audio.type === 'story' &&
        audio.audioType === 'audio' &&
        isAuthenticated
      ) {
        increasePlays({ variables: { storyId: audio.id } });
      }

      dispatch(resetPlayerThunk());
      dispatch(
        updateCurrentlyPlaying({
          ...audio,
          sourceUrl: pathname,
        }),
      );
      dispatch(requestTogglePlayThunk());
    },
    [dispatch, increasePlays, isAuthenticated, pathname],
  );

  const requestSeekBackward = React.useCallback(() => {
    dispatch(requestSeekBackwardThunk());
  }, [dispatch]);

  const requestSeekForward = React.useCallback(() => {
    dispatch(requestSeekForwardThunk());
  }, [dispatch]);

  const requestSeekToTime = React.useCallback(
    (seekTo: number) => {
      dispatch(requestSeekToTimeThunk(seekTo));
    },
    [dispatch],
  );

  const showFullPlayerCallback = React.useCallback(
    () => dispatch(showFullPlayer()),
    [dispatch],
  );

  const hideFullPlayerCallback = React.useCallback(
    () => dispatch(hideFullPlayer()),
    [dispatch],
  );

  const playSpeakerTeaser = React.useCallback(
    ({ id, speakerName, audioUrl }: PlaySpeakerTeaser) => {
      playAudio({
        id,
        type: 'speaker',
        audioType: 'teaser',
        speakerName,
        audioUrl,
      });
    },
    [playAudio],
  );

  const playStoryTeaser = React.useCallback(
    ({ id, audioUrl, speakerName, storyTitle }: PlayStoryTeaser) => {
      playAudio({
        id,
        type: 'story',
        audioType: 'teaser',
        speakerName,
        storyTitle,
        audioUrl,
      });
    },
    [playAudio],
  );

  const playOtherTeaser = React.useCallback(
    ({ id, audioUrl, otherTitle }: PlayOtherTeaser) => {
      playAudio({
        id,
        type: 'other',
        audioType: 'teaser',
        otherTitle,
        audioUrl,
      });
    },
    [playAudio],
  );

  const apolloClient = useApolloClient();
  const defaults = useGraphaelDefaults();

  const playStoryAudio = React.useCallback(
    async ({
      id,
      audioUrl,
      speakerName,
      storyTitle,
      algoliaSource,
      orgasms,
      contentType,
    }: PlayStoryAudio) => {
      if (audioUrl) {
        playAudio({
          id,
          type: 'story',
          audioType: 'audio',
          audioUrl,
          speakerName,
          storyTitle,
          algoliaSource,
          orgasms,
          contentType,
        });
      } else {
        const { data } = await apolloClient.query<GetStoryAudioUrlQuery>({
          ...defaults,
          query: GetStoryAudioUrlDocument,
          variables: { storyId: id },
        });

        if (data?.story?.transcodedAudioUrl) {
          playAudio({
            id: data.story.id,
            type: 'story',
            audioType: 'audio',
            audioUrl: data.story.transcodedAudioUrl,
            storyTitle: data.story.title,
            speakerName: data.story.speaker.name,
            orgasms: data.story.orgasms,
            contentType: data.story.contentType.slug as ContentType,
          });
        }
      }
    },
    [apolloClient, defaults, playAudio],
  );

  const playSeriesAudio = React.useCallback(
    async ({
      id,
      audioUrl,
      speakerName,
      storyTitle,
      algoliaSource,
      orgasms,
      contentType,
    }: PlayStoryAudio) => {
      if (audioUrl) {
        playAudio({
          id,
          type: 'series',
          audioType: 'audio',
          audioUrl,
          speakerName,
          storyTitle,
          algoliaSource,
          orgasms,
          contentType,
        });
      } else {
        const { data } = await apolloClient.query<GetStoryAudioUrlQuery>({
          ...defaults,
          query: GetStoryAudioUrlDocument,
          variables: { storyId: id },
        });

        if (data?.story?.transcodedAudioUrl) {
          playAudio({
            id: data.story.id,
            type: 'series',
            audioType: 'audio',
            audioUrl: data.story.transcodedAudioUrl,
            description: data.story.description,
            storyTitle: data.story.title,
            speakerName: data.story.speaker.name,
            orgasms: data.story.orgasms,
            contentType: data.story.contentType.slug as ContentType,
          });
        }
      }
    },
    [apolloClient, defaults, playAudio],
  );

  return {
    currentlyPlaying: player.currentlyPlaying,
    isPlaying: player.playing && !player.seeking,
    isLoading: player.audioLoading || (player.playing && player.seeking),
    fullAudioPlayed: player.fullAudioPlayed,
    mediaDuration: player.mediaDuration,
    currentTime: player.currentTime,
    isFullPlayer: player.fullPlayer,
    isPlayingSeries: player.currentlyPlaying?.type === 'series',
    isMyAudio,
    togglePlay,
    stopPlayback,
    hideFullPlayer: hideFullPlayerCallback,
    playSpeakerTeaser,
    playStoryTeaser,
    playOtherTeaser,
    playStoryAudio,
    playSeriesAudio,
    showFullPlayer: showFullPlayerCallback,
    requestSeekBackward,
    requestSeekForward,
    requestSeekToTime,
    autoPlay: player.autoPlay,
  };
};
